import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import DOMPurify from 'dompurify';
import { CmsButton, CmsImage, RichText, SectionShell, SectionHeader, isDarkBg, videoEmbed, SHADCN_BTN } from '../shared';

export const TextSection = ({ section }) => {
  const p = section.props || {};
  const dark = isDarkBg(section.style);
  const layout = section.layout || 'prose';
  const width = layout === 'centered' ? 'text-center' : layout === 'two_column' ? 'max-w-none' : 'max-w-4xl';
  return (
    <SectionShell section={section} container={layout === 'centered' ? 'lg' : 'xl'}>
      <div className={width}>
        {p.eyebrow && <span className={`label-overline ${dark ? 'text-[color:var(--site-accent-light)]' : 'text-[color:var(--site-accent)]'}`}>{p.eyebrow}</span>}
        {p.title && <h2 className={`${layout === 'centered' ? 'mt-6 max-w-4xl mx-auto' : 'mt-3'} ${p.body ? 'mb-8' : ''} ${dark ? 'text-white' : 'text-neutral-900'} ${layout === 'prose' ? 'line-accent' : ''}`}>{p.title}</h2>}
        <RichText html={p.body} className={`${layout === 'two_column' ? 'md:columns-2 md:gap-12' : ''} text-lg leading-relaxed ${dark ? 'text-neutral-300' : 'text-neutral-600'}`} />
      </div>
    </SectionShell>
  );
};

export const TextImageSection = ({ section }) => {
  const p = section.props || {};
  const dark = isDarkBg(section.style);
  const layout = section.layout || 'image_right';
  const imageLeft = layout.startsWith('image_left');
  const large = layout.endsWith('_large');
  const darkText = dark && layout !== 'card';

  if (layout === 'overlay') {
    return (
      <section id={section.id} data-testid="section-text_image" className="relative min-h-[70vh] flex items-end text-white">
        <CmsImage image={p.image} className="absolute inset-0 w-full h-full object-cover rounded-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/90 via-neutral-900/40 to-transparent" />
        <div className="container-xl relative z-10 pb-20 pt-40">
          <div className="max-w-2xl">
            {p.eyebrow && <span className="label-overline text-[color:var(--site-accent-light)]">{p.eyebrow}</span>}
            {p.title && <h2 className="mt-3 text-white">{p.title}</h2>}
            <RichText html={p.body} className="mt-5 text-lg text-neutral-200" />
            <div className="mt-8"><CmsButton button={p.button} variant="light" /></div>
          </div>
        </div>
      </section>
    );
  }

  const text = (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className={layout === 'card' ? 'p-8 lg:p-12 bg-white border border-neutral-200 rounded-[var(--site-radius)]' : ''}>
      {p.eyebrow && <span className={`label-overline ${dark ? 'text-[color:var(--site-accent-light)]' : 'text-[color:var(--site-accent)]'}`}>{p.eyebrow}</span>}
      {p.title && <h2 className={`mb-6 ${dark && layout !== 'card' ? 'text-white' : 'text-neutral-900'} ${p.eyebrow ? 'mt-4' : 'line-accent'}`}>{p.title}</h2>}
      <RichText html={p.body} className={`${darkText ? '!space-y-6 text-neutral-300' : 'text-neutral-600'}`} />
      {p.bullets?.length > 0 && (
        <ul className={darkText ? 'mt-8 space-y-4' : 'mt-6 space-y-3'}>
          {p.bullets.map((b, i) => (
            <li key={i} className={`flex items-center gap-3 ${darkText ? 'text-neutral-300' : 'text-neutral-700'}`}>{darkText ? <div className="w-1.5 h-1.5 bg-amber-400 flex-shrink-0" /> : <Check className="w-5 h-5 text-[color:var(--site-accent)] flex-shrink-0" />}<span>{b.text}</span></li>
          ))}
        </ul>
      )}
      {p.button?.text && <div className={darkText ? 'mt-10' : 'mt-8'}>{darkText ? <CmsButton button={p.button} className={`${SHADCN_BTN} !bg-white !text-neutral-900 hover:!bg-neutral-100 normal-case tracking-normal`} icon={false} /> : <CmsButton button={p.button} variant="primary" />}</div>}
    </motion.div>
  );
  const image = (
    <div className="relative">
      <CmsImage image={p.image} fallbackAlt={p.title} className={`w-full object-cover ${large ? 'aspect-[4/5]' : 'aspect-[4/3]'}`} />
      {p.badge_value && (
        <div className="absolute -bottom-6 -left-6 bg-amber-600 text-white p-6 hidden lg:block" data-testid="text-image-badge">
          <p className="!text-4xl font-serif font-semibold">{p.badge_value}</p>
          {p.badge_label && <p className="text-sm uppercase tracking-wider mt-1">{p.badge_label}</p>}
        </div>
      )}
    </div>
  );
  const cols = large ? (imageLeft ? 'lg:grid-cols-[3fr_2fr]' : 'lg:grid-cols-[2fr_3fr]') : 'lg:grid-cols-2';
  return (
    <SectionShell section={section}>
      <div className={`grid ${cols} gap-12 lg:gap-20 items-center`}>
        {imageLeft ? <>{image}{text}</> : <><div className="lg:order-1">{text}</div><div className="lg:order-2">{image}</div></>}
      </div>
    </SectionShell>
  );
};

export const ImageSection = ({ section }) => {
  const p = section.props || {};
  const full = section.layout === 'full_width';
  return (
    <SectionShell section={section} container={full ? 'none' : 'xl'}>
      <figure>
        <CmsImage image={p.image} className={`w-full object-cover ${full ? 'max-h-[80vh] rounded-none' : 'max-h-[70vh]'} ${section.layout === 'rounded' ? 'rounded-2xl' : ''}`} />
        {p.caption && <figcaption className="mt-4 text-sm text-neutral-500 text-center">{p.caption}</figcaption>}
      </figure>
    </SectionShell>
  );
};

export const VideoSection = ({ section }) => {
  const p = section.props || {};
  const v = videoEmbed(p.video_url);
  return (
    <SectionShell section={section} container={section.layout === 'full_width' ? 'none' : 'xl'}>
      <SectionHeader props={{ title: p.title }} style={{ align: 'center' }} dark={isDarkBg(section.style)} />
      {v && (
        <div className="aspect-video w-full overflow-hidden rounded-[var(--site-radius)] bg-black">
          {v.type === 'iframe' ? <iframe title={p.title || 'video'} src={v.src} className="w-full h-full" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen />
            : <video src={v.src} controls poster={p.poster?.url} className="w-full h-full" />}
        </div>
      )}
    </SectionShell>
  );
};

export const EmbedSection = ({ section }) => {
  const p = section.props || {};
  return (
    <SectionShell section={section} container={section.layout === 'full_width' ? 'none' : 'xl'}>
      <SectionHeader props={{ title: p.title }} dark={isDarkBg(section.style)} />
      <div className="cms-embed [&_iframe]:w-full [&_iframe]:h-96" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.html || '', { ADD_TAGS: ['iframe'], ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'loading', 'referrerpolicy', 'src', 'style'] }) }} />
    </SectionShell>
  );
};

export const DividerSection = ({ section }) => {
  const l = section.layout || 'space_sm';
  if (l === 'line') return <div className="container-xl" data-testid="section-divider"><hr className="border-neutral-200" /></div>;
  return <div data-testid="section-divider" className={l === 'space_lg' ? 'h-24 lg:h-32' : 'h-10 lg:h-14'} />;
};
